import { isAfter } from '@chilipiper/date-time'
import { useXMPPClientContext } from './XMPPClientContext'

type AffiliationEvent = {
  roomId: string
  userId: string
  affiliation: 'member' | 'none' | 'owner'
  /** There's no version if current user is removed from the room */
  version?: string
  previousVersion?: string
  /** Timestamp is present only on archive affiliation change */
  timestamp?: string
}

export type RoomAssignees = Partial<
  Record<
    string,
    {
      version?: string
      timestamp?: string
      assignees: Partial<Record<string, 'owner' | 'member' | 'none'>>
    }
  >
>

export const getNewRoomAssignees = ({
  prevRoomAssignees,
  affiliationEvent,
}: {
  prevRoomAssignees: RoomAssignees
  affiliationEvent: AffiliationEvent
}): typeof prevRoomAssignees => {
  const isUserKickedOut = affiliationEvent.version === undefined

  if (isUserKickedOut) {
    return {
      ...prevRoomAssignees,
      [affiliationEvent.roomId]: {
        version: undefined,
        timestamp: undefined,
        assignees: {
          [affiliationEvent.userId]: 'none',
        },
      },
    }
  }

  const prevRoom = prevRoomAssignees[affiliationEvent.roomId]

  const isUserWithUnknownStatus = prevRoom?.assignees[affiliationEvent.userId] === undefined
  const isNewTimestamp =
    affiliationEvent.timestamp !== undefined &&
    (prevRoom?.timestamp === undefined ||
      isAfter(new Date(affiliationEvent.timestamp), new Date(prevRoom.timestamp)))
  const isNewVersion = affiliationEvent.timestamp === undefined
  const isNew = (isNewTimestamp && prevRoom?.version === undefined) || isNewVersion

  if (isUserWithUnknownStatus || isNew) {
    return {
      ...prevRoomAssignees,
      [affiliationEvent.roomId]: {
        version: isNewVersion ? affiliationEvent.version : prevRoom?.version,
        timestamp: isNewTimestamp ? affiliationEvent.timestamp : prevRoom?.timestamp,
        assignees: {
          ...prevRoom?.assignees,
          [affiliationEvent.userId]: affiliationEvent.affiliation,
        },
      },
    }
  }
  return prevRoomAssignees
}
const isAssignee = (userId: string, tenantId: string, workspaceId: string | undefined) =>
  userId !== tenantId &&
  userId !== workspaceId &&
  !userId.endsWith('_guest') &&
  !userId.endsWith('_bot')

export const useRoomAssignee = ({
  roomId,
  tenantId,
  workspaceId,
}: {
  roomId: string
  tenantId: string
  workspaceId: string
}) => {
  const { client, publicClient } = useXMPPClientContext()
  const assigneeIds = Object.entries({
    ...client.roomAssignees[roomId]?.assignees,
    ...publicClient.roomAssignees[roomId]?.assignees,
  })
    .filter(
      ([userId, affiliation]) => affiliation !== 'none' && isAssignee(userId, tenantId, workspaceId)
    )
    .map(([userId, _]) => userId)
  return assigneeIds?.length === 1 ? assigneeIds[0] : undefined
}
